import { Bell, X } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications } from '../lib/notifications.tsx';
import { ICONS } from './NotificationBell.tsx';

/* La bulle qui annonce une notification par-dessus l'écran en cours.
 *
 * ── Pourquoi elle existe alors qu'il y a déjà la cloche ────────────────────
 *
 * La cloche est un COMPTEUR : elle change discrètement dans un coin, et sur un
 * téléphone posé sur le chariot, en plein soleil, personne ne voit une
 * pastille de huit pixels changer de chiffre. Une invitation à jouer arrive
 * pendant qu'on regarde autre chose ; si elle n'interrompt pas, elle est
 * manquée, et la partie part sans le joueur.
 *
 * La bannière système, elle, ne couvre pas ce cas : le téléphone ne l'affiche
 * pas — ou l'efface aussitôt — quand l'application est déjà à l'écran.
 *
 * ── En haut, sous le pouce ─────────────────────────────────────────────────
 *
 * Posée en haut parce que le bas de l'écran appartient aux boutons de jeu :
 * une bulle qui recouvre le pavé des coups ferait rater une balle. Mais elle
 * se touche sur toute sa surface, et la croix a une cible élargie : une seule
 * main, gantée, doit suffire pour l'ouvrir comme pour l'écarter.
 */

/** Combien de temps la bulle reste. Assez pour être lue en relevant la tête,
 *  assez peu pour ne pas devenir un bandeau permanent. */
const DUREE_MS = 8000;

export function NotificationToast() {
    const { toast, dismissToast, markRead } = useNotifications();
    const navigate = useNavigate();

    useEffect(() => {
        if (!toast) return undefined;
        const t = setTimeout(dismissToast, DUREE_MS);
        return () => clearTimeout(t);
    }, [toast, dismissToast]);

    if (!toast) return null;
    const Icon = ICONS[toast.kind] ?? Bell;

    // Ouvrir vaut lecture : on ne redemandera pas au joueur de marquer lu ce
    // qu'il vient d'aller voir.
    const ouvrir = (): void => {
        dismissToast();
        void markRead(toast.id);
        if (toast.link) navigate(toast.link);
    };

    return (
        <div className="safe-top pointer-events-none fixed inset-x-0 top-0 z-50 flex justify-center px-3 pt-2">
            <div
                role="status"
                className="pointer-events-auto flex w-full max-w-md items-center gap-3 rounded-box border border-primary/30 bg-base-100 px-3 py-3 text-base-content shadow-lg motion-safe:animate-[toast_240ms_ease-out]"
            >
                <button
                    type="button"
                    onClick={ouvrir}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                >
                    <span className="grid size-10 shrink-0 place-items-center rounded-full bg-primary/15 text-primary">
                        <Icon size={22} />
                    </span>
                    <span className="min-w-0 flex-1">
                        <span className="block truncate font-bold leading-tight">{toast.title}</span>
                        {toast.body && (
                            <span className="block truncate text-sm leading-tight opacity-70">
                                {toast.body}
                            </span>
                        )}
                    </span>
                </button>
                <button
                    type="button"
                    onClick={dismissToast}
                    aria-label="Masquer"
                    className="relative shrink-0 opacity-50 before:absolute before:-inset-3 before:content-['']"
                >
                    <X size={20} />
                </button>
            </div>
        </div>
    );
}
